import {
  format,
  isAfter,
  isBefore,
  isSameDay as isSameDayFns,
  isSameMonth as isSameMonthDateFns,
  isSameYear as isSameYearDateFns,
  lastDayOfMonth,
  lastDayOfYear,
} from 'date-fns';
import { isDate } from 'lodash';

export const DATE_FORMAT_VALIDATION_REGEX = /^(0[1-9]|1[0-2])\/(0[1-9]|[1-2][0-9]|3[0-1])\/\d{4}$/;

export const MINUTES_IN_HOUR = 60;

export const MSECS_IN_SEC = 1000;

export const DEFAULT_DATE_FORMAT = 'MMM d, yyyy';

export const LARGE_DATE_FORMAT = 'MMMM d, yyyy';

export const DEFAULT_TIME_FORMAT = 'h:mmaaa';

export const CHART_KEY_DAILY_DATE_FORMAT = 'yyyy-MM-dd';

export const ISO_ONLY_DATE_FORMAT = "yyyy-MM-dd'T00:00:00.000Z'";

export const toLocaleDateString = (date: Date): string => {
  return date?.toLocaleDateString('en-US', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const padTo2Digits = (num: number): string => {
  return num.toString().padStart(2, '0');
};

const removeTimeFromIso = (isoString: string): string => {
  return isoString.split('T', 1)[0];
};

const removeTimeFromDate = (date: Date | string = new Date()): string => {
  return isDate(date)
    ? [date.getFullYear(), padTo2Digits(date.getMonth() + 1), padTo2Digits(date.getDate())].join('-')
    : removeTimeFromIso(date);
};

export const formatDate = (date: Date | string = new Date(), dateFormat = DEFAULT_DATE_FORMAT): string => {
  try {
    return format(new Date(date), dateFormat);
  } catch (_err) {
    return '';
  }
};

export const formatTime = (datetime: string, timezone: string): string => {
  return new Date(datetime)
    .toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: timezone,
    })
    .toLowerCase()
    .replace(' ', '')
    .replace(':00', '');
};

export const getISODateWithoutTZ = (date: string | Date): string => {
  return getDateWithoutTZ(removeTimeFromDate(new Date(date)), ISO_ONLY_DATE_FORMAT);
};

export const getDateWithoutTZ = (stringDateWithTimezone: string, dateFormat: string = DEFAULT_DATE_FORMAT): string => {
  const date = new Date(stringDateWithTimezone);
  const dateOnly = new Date(date.valueOf() + date.getTimezoneOffset() * MINUTES_IN_HOUR * MSECS_IN_SEC);
  return format(dateOnly, dateFormat);
};

export const clearTimeFromDate = (date: Date): Date => {
  return new Date(new Date(date).toDateString());
};

export const getFirstDayOfWeek = (date: Date = new Date()): Date => {
  return clearTimeFromDate(new Date(new Date(date).setDate(date.getDate() - date.getDay())));
};

export const getLastDayOfWeek = (date: Date = new Date()): Date => {
  return clearTimeFromDate(new Date(new Date(date).setDate(date.getDate() - date.getDay() + 6)));
};

export const getFirstDayOfMonth = (date: Date = new Date()): Date => {
  date.setDate(1);
  return clearTimeFromDate(date);
};

export const getLastDayOfMonth = (date: Date = new Date()): Date => {
  return clearTimeFromDate(lastDayOfMonth(date));
};

export const getFirstDayOfPreviousMonth = (date: Date = new Date()): Date => {
  date.setDate(0);
  date.setDate(1);
  return clearTimeFromDate(date);
};

export const getLastDayOfPreviousMonth = (date: Date = new Date()): Date => {
  date.setDate(0);
  return clearTimeFromDate(date);
};

export const getFirstDayOfYear = (date: Date = new Date()): Date => {
  return clearTimeFromDate(new Date(date.getFullYear(), 0, 1));
};

export const getLastDayOfYear = (date: Date = new Date()): Date => {
  return clearTimeFromDate(lastDayOfYear(date));
};

export const getFirstDayOfQuarter = (date: Date = new Date()): Date => {
  const quarter = Math.floor(date.getMonth() / 3);
  return clearTimeFromDate(new Date(date.getFullYear(), quarter * 3, 1));
};

export const getLastDayOfQuarter = (date: Date = new Date()): Date => {
  const quarter = Math.floor(date.getMonth() / 3);
  return clearTimeFromDate(new Date(date.getFullYear(), (quarter + 1) * 3, 0));
};

export const getSelectedDateRangeString = (startDate: Date, endDate: Date, shortFormat = false): string => {
  const isSameDay = isSameDayFns(startDate, endDate);
  const isSameMonth = isSameMonthDateFns(startDate, endDate);
  const isSameYear = isSameYearDateFns(startDate, endDate);

  if (isSameDay) {
    const startDateString = formatDate(startDate, shortFormat ? 'MMM d' : 'MMMM d');

    const dates = `${startDateString}`;
    return shortFormat ? dates : `${dates}, ${startDate.getFullYear()}`;
  }

  if (isSameMonth) {
    const startDateString = formatDate(startDate, shortFormat ? 'MMM d' : 'MMMM d');
    const endDateString = formatDate(endDate, 'd');

    const dates = `${startDateString} - ${endDateString}`;
    return shortFormat ? dates : `${dates}, ${startDate.getFullYear()}`;
  }

  if (!isSameMonth && isSameYear) {
    const startDateString = formatDate(startDate, 'MMM d');
    const endDateString = formatDate(endDate, 'MMM d');

    const dates = `${startDateString} - ${endDateString}`;
    return shortFormat ? dates : `${dates}, ${startDate.getFullYear()}`;
  }

  const startDateString = formatDate(startDate, shortFormat ? 'MMM d' : DEFAULT_DATE_FORMAT);
  const endDateString = formatDate(endDate, shortFormat ? 'MMM d' : DEFAULT_DATE_FORMAT);
  return `${startDateString} - ${endDateString}`;
};

export const isDateBetween = (targetDate: Date, startDate: Date, endDate: Date): boolean => {
  return (
    (isSameDayFns(targetDate, startDate) || isAfter(targetDate, startDate)) &&
    (isSameDayFns(targetDate, endDate) || isBefore(targetDate, endDate))
  );
};

export const getFormattedDate = (date?: Date, clearFormat: boolean = true): string | undefined => {
  if (!date) return undefined;
  return clearFormat ? getDateWithoutTZ(date.toDateString(), 'yyyy-MM-dd') : getISODateWithoutTZ(date);
};
